const operation = (query, variables) => ({ query, variables });

const UpdateProfessionalStatus = ({ professionalStatus, professionalUniqueID }) =>
  operation(
    `mutation UpdateProfessionalStatus($data: ProfessionalUpdateInput!, $where: ProfessionalWhereUniqueInput!) {
      updateProfessional(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: { status: professionalStatus },
      where: { id: professionalUniqueID },
    }
  );

const RejectProfessionalRequest = ({ id }) =>
  operation(
    `mutation RejectProfessionalRequest($where: ProfessionalWhereUniqueInput!) {
      rejectProfessionalRequest(where: $where) {
        id
        status
      }
    }`,
    { where: { id } }
  );

const UpdateProfessionalActiveStatus = ({ isActive, id }) =>
  operation(
    `mutation UpdateProfessionalActiveStatus($data: ProfessionalUpdateInput!, $where: ProfessionalWhereUniqueInput!) {
      updateProfessional(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: { isActive },
      where: { id },
    }
  );

const UpdateOrderStatus = ({ orderStatus, orderUniqueID }) =>
  operation(
    `mutation UpdateOrderStatus($data: OrderUpdateInput!, $where: OrderWhereUniqueInput!) {
      updateOrder(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: { status: orderStatus },
      where: { id: orderUniqueID },
    }
  );

const UpdateAccountDetails = ({ ownerName, bankName, branch, accountNo, serviceID }) =>
  operation(
    `mutation UpdateAccountDetails($data: ServiceUpdateInput!, $where: ServiceWhereUniqueInput!) {
      updateService(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: {
        accountName: ownerName,
        bankName,
        bankBranchName: branch,
        accountNumber: accountNo,
      },
      where: { id: serviceID },
    }
  );

const createProfessional = ({
  firstName,
  lastName,
  email,
  address,
  province,
  postalCode,
  district,
  mobileNumber,
  avatar,
  nicImage,
  addressProof,
  fbUid,
  about,
}) =>
  operation(
    `mutation CreateProfessional($data: ProfessionalCreateInput!) {
      createProfessional(data: $data) {
        id
      }
    }`,
    {
      data: {
        avatar,
        nicImage,
        addressProof,
        firstName,
        lastName,
        postalCode,
        district,
        addressLine1: address,
        province,
        email,
        mobileNumber,
        about,
        fbUid,
      },
    }
  );

// Rolls back the caller's own un-reviewed professional row when a later
// registration step fails (server-side, scoped to the caller's fb_uid).
const RollbackProfessionalRegistration = () =>
  operation(
    `mutation RollbackProfessionalRegistration {
      rollbackProfessionalRegistration
    }`,
    {}
  );

const UpdateProfessional = ({
  professionalId,
  firstName,
  lastName,
  avatar,
  postalCode,
  province,
  address,
  about,
}) =>
  operation(
    `mutation UpdateProfessional($data: ProfessionalUpdateInput!, $where: ProfessionalWhereUniqueInput!) {
      updateProfessional(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: {
        firstName,
        lastName,
        avatar,
        postalCode,
        province,
        addressLine1: address,
        about,
      },
      where: { id: professionalId },
    }
  );

const createService = ({
  name,
  categoryId,
  icon,
  images = [],
  hotlineNumber,
  location,
  description,
  professionalId,
  bannerImage,
  facebookUrl,
  websiteUrl,
}) =>
  operation(
    `mutation CreateService($data: ServiceCreateInput!) {
      createService(data: $data) {
        id
      }
    }`,
    {
      data: {
        name,
        categoryId,
        icon,
        bannerImage,
        images,
        hotlineNumber,
        location,
        description,
        facebookUrl,
        websiteUrl,
        professionalId,
      },
    }
  );

const UpdateService = ({ location, name, websiteUrl, id, bannerImage, icon, images = [] }) =>
  operation(
    `mutation UpdateService($data: ServiceUpdateInput!, $where: ServiceWhereUniqueInput!) {
      updateService(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: {
        name,
        location,
        websiteUrl,
        bannerImage,
        icon,
        images,
      },
      where: { id },
    }
  );

const UpdateUser = ({ id, firstName, lastName, address, postalCode, province }) =>
  operation(
    `mutation UpdateUser($data: UserUpdateInput!, $where: UserWhereUniqueInput!) {
      updateUser(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: {
        firstName,
        lastName,
        addressLine1: address,
        postalCode,
        province,
      },
      where: { id },
    }
  );

const UpdateEventBookerActiveState = ({ id, isActive }) =>
  operation(
    `mutation UpdateEventBookerActiveState($data: EventBookerUpdateInput!, $where: EventBookerWhereUniqueInput!) {
      updateEventBooker(where: $where, data: $data) {
        id
      }
    }`,
    {
      data: { isActive },
      where: { id },
    }
  );

const CreateReview = ({ orderId, serviceId, servicePackageId, description, rating }) =>
  operation(
    `mutation CreateReview($data: UserReviewCreateInput!) {
      createUserReview(data: $data) {
        id
      }
    }`,
    {
      data: {
        rating: Number(rating),
        description,
        orderId,
        serviceId: serviceId ?? '',
        servicePackageId: servicePackageId ?? '',
      },
    }
  );

const CreateUser = ({
  postalCode,
  firstName,
  lastName,
  province,
  mobileNumber,
  addressLine1,
  fbUID,
  email,
}) =>
  operation(
    `mutation CreateUser($data: UserCreateInput!) {
      createUser(data: $data) {
        id
      }
    }`,
    {
      data: {
        firstName,
        lastName,
        postalCode,
        addressLine1,
        province,
        email,
        mobileNumber,
        fbUid: fbUID,
      },
    }
  );

const UpdateReviewActiveState = ({ isActive, id }) =>
  operation(
    `mutation UpdateReviewActiveState($data: ReviewUpdateInput!, $where: ReviewWhereUniqueInput!) {
      updateReview(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: { isActive },
      where: { id },
    }
  );

const CreateServicePackage = ({ title, serviceId, price, description }) =>
  operation(
    `mutation CreateServicePackage($data: ServicePackageCreateInput!) {
      createServicePackage(data: $data) {
        id
      }
    }`,
    {
      data: {
        name: title,
        price: Number(price),
        description,
        serviceId,
      },
    }
  );

function UpdateServicePackage({ title, packageId, description, price }) {
  return operation(
    `mutation UpdateServicePackage($data: ServicePackageUpdateInput!, $where: ServicePackageWhereUniqueInput!) {
      updateServicePackage(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: {
        name: title,
        price: Number(price),
        description,
      },
      where: { id: packageId },
    }
  );
}

function UpdateServicePackageActiveState({ id, isActive }) {
  return operation(
    `mutation UpdateServicePackageActiveState($data: ServicePackageUpdateInput!, $where: ServicePackageWhereUniqueInput!) {
      updateServicePackage(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: { isActive },
      where: { id },
    }
  );
}

const CreateOrder = ({ serviceId, total, date, time, note, place, servicePackageId }) =>
  operation(
    `mutation CreateOrder($data: OrderCreateInput!) {
      createOrder(data: $data) {
        id
      }
    }`,
    {
      data: {
        date,
        time,
        note,
        place,
        orderTotal: Number(total),
        serviceId,
        servicePackageIds: [servicePackageId],
      },
    }
  );

const UpdateServiceIsPopular = ({ isPopular, serviceId }) =>
  operation(
    `mutation UpdateServiceIsPopular($data: ServiceUpdateInput!, $where: ServiceWhereUniqueInput!) {
      updateService(where: $where, data: $data) {
        id
      }
    }`,
    {
      data: { isPopular },
      where: { id: serviceId },
    }
  );

const UpdateProfessionalNicAndAddrProof = ({ professionalID, nicImgURL, addrImgURL }) =>
  operation(
    `mutation UpdateProfessionalNicAndAddrProof($data: ProfessionalUpdateInput!, $where: ProfessionalWhereUniqueInput!) {
      updateProfessional(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: { addressProof: addrImgURL, nicImage: nicImgURL },
      where: { id: professionalID },
    }
  );

const UpdateCompanyInformation = ({
  facebookUrl,
  instagramUrl,
  twitterUrl,
  telegramUrl,
  whatsappUrl,
  mobileNumber,
  email,
  logo,
  bannerImage,
}) =>
  operation(
    `mutation UpdateCompanyInformation($data: CompanyUpdateInput!, $where: CompanyWhereUniqueInput!) {
      updateCompany(data: $data, where: $where) {
        id
        email
        phoneNumber
        socialMedia
        logo
        bannerImage
      }
    }`,
    {
      data: {
        phoneNumber: mobileNumber || null,
        email: email || null,
        socialMedia: [facebookUrl, instagramUrl, twitterUrl, telegramUrl, whatsappUrl],
        logo: logo ?? null,
        bannerImage: bannerImage ?? null,
      },
      where: {},
    }
  );

// Admin — service package CRUD with features (serviceId lives in data).
const AdminCreateServicePackage = ({
  serviceId,
  name,
  price,
  description,
  features,
  isActive,
}) =>
  operation(
    `mutation AdminCreateServicePackage($data: ServicePackageCreateInput!) {
      createServicePackage(data: $data) {
        id
      }
    }`,
    {
      data: { serviceId, name, price: Number(price), description, features, isActive },
    }
  );

const AdminUpdateServicePackage = ({ id, name, price, description, features, isActive }) =>
  operation(
    `mutation AdminUpdateServicePackage($data: ServicePackageUpdateInput!, $where: ServicePackageWhereUniqueInput!) {
      updateServicePackage(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: {
        name,
        price: price === undefined ? undefined : Number(price),
        description,
        features,
        isActive,
      },
      where: { id },
    }
  );

const AdminDeleteServicePackage = ({ id }) =>
  operation(
    `mutation AdminDeleteServicePackage($where: ServicePackageWhereUniqueInput!) {
      deleteServicePackage(where: $where) {
        id
      }
    }`,
    { where: { id } }
  );

// Admin — change a service's category (backend enforces one-per-category).
const AdminUpdateServiceCategory = ({ id, categoryId }) =>
  operation(
    `mutation AdminUpdateServiceCategory($data: ServiceUpdateInput!, $where: ServiceWhereUniqueInput!) {
      updateService(data: $data, where: $where) {
        id
        categoryId
      }
    }`,
    { data: { categoryId }, where: { id } }
  );

// Professional dashboard — toggle a service's active state.
const ProSetServiceActive = ({ id, isActive }) =>
  operation(
    `mutation ProSetServiceActive($data: ServiceUpdateInput!, $where: ServiceWhereUniqueInput!) {
      updateService(data: $data, where: $where) {
        id
      }
    }`,
    { data: { isActive }, where: { id } }
  );

// Professional dashboard — package CRUD (user-api takes serviceId as a top-level arg).
const ProCreateServicePackage = ({
  serviceId,
  name,
  price,
  description,
  features,
  isActive,
}) =>
  operation(
    `mutation ProCreateServicePackage($data: ServicePackageCreateInput!, $serviceId: ID!) {
      createServicePackage(data: $data, serviceId: $serviceId) {
        id
      }
    }`,
    {
      data: { name, price: Number(price), description, features, isActive },
      serviceId,
    }
  );

const ProUpdateServicePackage = ({ id, name, price, description, features, isActive }) =>
  operation(
    `mutation ProUpdateServicePackage($data: ServicePackageUpdateInput!, $where: ServicePackageWhereUniqueInput!) {
      updateServicePackage(data: $data, where: $where) {
        id
      }
    }`,
    {
      data: {
        name,
        price: price === undefined ? undefined : Number(price),
        description,
        features,
        isActive,
      },
      where: { id },
    }
  );

const ProDeleteServicePackage = ({ id }) =>
  operation(
    `mutation ProDeleteServicePackage($where: ServicePackageWhereUniqueInput!) {
      deleteServicePackage(where: $where) {
        id
      }
    }`,
    { where: { id } }
  );

// Admin — category CRUD.
const CreateCategory = ({ name, slug, description, icon, isActive }) =>
  operation(
    `mutation CreateCategory($data: CategoryCreateInput!) {
      createCategory(data: $data) {
        id
        name
        slug
        serviceCount
      }
    }`,
    { data: { name, slug, description, icon, isActive } }
  );

const UpdateCategory = ({ id, name, slug, description, icon, isActive, showHome }) =>
  operation(
    `mutation UpdateCategory($data: CategoryUpdateInput!, $where: CategoryWhereUniqueInput!) {
      updateCategory(data: $data, where: $where) {
        id
        name
        slug
        isActive
        showHome
        serviceCount
      }
    }`,
    { data: { name, slug, description, icon, isActive, showHome }, where: { id } }
  );

const DeleteCategory = ({ id }) =>
  operation(
    `mutation DeleteCategory($where: CategoryWhereUniqueInput!) {
      deleteCategory(where: $where) {
        id
      }
    }`,
    { where: { id } }
  );

const CreateUploadUrl = ({ contentType, folder }) =>
  operation(
    `mutation CreateUploadUrl($data: CreateUploadUrlInput!) {
      createUploadUrl(data: $data) {
        uploadUrl
        fileUrl
        key
      }
    }`,
    { data: { contentType, folder } }
  );

const DeleteUpload = ({ url }) =>
  operation(
    `mutation DeleteUpload($url: String!) {
      deleteUpload(url: $url)
    }`,
    { url }
  );

export {
  AdminCreateServicePackage,
  AdminUpdateServicePackage,
  AdminDeleteServicePackage,
  AdminUpdateServiceCategory,
  ProSetServiceActive,
  ProCreateServicePackage,
  ProUpdateServicePackage,
  ProDeleteServicePackage,
  CreateCategory,
  UpdateCategory,
  DeleteCategory,
  UpdateProfessionalStatus,
  UpdateProfessionalActiveStatus,
  UpdateOrderStatus,
  UpdateAccountDetails,
  createProfessional,
  UpdateProfessional,
  RollbackProfessionalRegistration,
  createService,
  UpdateService,
  UpdateUser,
  CreateReview,
  CreateUser,
  UpdateReviewActiveState,
  CreateServicePackage,
  UpdateServicePackage,
  CreateOrder,
  UpdateServiceIsPopular,
  UpdateProfessionalNicAndAddrProof,
  UpdateCompanyInformation,
  UpdateEventBookerActiveState,
  RejectProfessionalRequest,
  UpdateServicePackageActiveState,
  CreateUploadUrl,
  DeleteUpload,
};
